import {
  InventoryRecord,
  InventoryCategoriesRecord,
} from '../types/pocketbase-types';
import {
  DataTable,
  type DataTableSortStatus,
  useDataTableColumns,
} from 'mantine-datatable';
import { Button, Group, NumberInput, Stack } from '@mantine/core';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { usePriceRangeFilter } from '../hooks/usePriceRange.hook.ts';
import { useInventoryColumns } from './InventoryColumns';
import { TABLE_CONSTANTS } from '../constants/table';
import { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import EditInventoryModal from './EditInventoryModal';
import { notifications } from '@mantine/notifications';
import { useGetCategoryRecords } from '../hooks/database.hooks';
import { ListResult } from 'pocketbase';

interface InventoryTableProps {
  inventoryQuery: UseQueryResult<
    ListResult<InventoryRecord & { expand: { categories: string[] } }>
  >;
  categoriesQuery: UseQueryResult<string[]>;
  manufacturersQuery: UseQueryResult<string[]>;
  minMaxPrices: [number, number] | undefined;
  deleteItemMutation: UseMutationResult<any, Error, string>;
  updateItemMutation: UseMutationResult<
    any,
    Error,
    { id: string; formData: FormData }
  >;
  page: number;
  setPage: (page: number) => void;
  sortStatus: DataTableSortStatus;
  setSortStatus: (status: DataTableSortStatus) => void;
  nameQuery: string;
  setNameQuery: (query: string) => void;
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  selectedManufacturers: string[];
  setSelectedManufacturers: (manufacturers: string[]) => void;
  selectedCurrencyTypes: {
    selling: string[];
    purchase: string[];
    estimated: string[];
  };
  setSelectedCurrencyTypes: (
    type: 'selling' | 'purchase' | 'estimated',
    currencies: string[]
  ) => void;
  priceRange: [number | null, number | null] | null;
  setPriceRange: (range: [number | null, number | null] | null) => void;
  selectedCurrencies: string[];
  setSelectedCurrencies: (currencies: string[]) => void;
}

export default function InventoryTable({
  inventoryQuery,
  categoriesQuery,
  manufacturersQuery,
  minMaxPrices,
  deleteItemMutation,
  updateItemMutation,
  page,
  setPage,
  sortStatus,
  setSortStatus,
  nameQuery,
  setNameQuery,
  selectedCategories,
  setSelectedCategories,
  selectedManufacturers,
  setSelectedManufacturers,
  selectedCurrencyTypes,
  setSelectedCurrencyTypes,
  priceRange,
  setPriceRange,
  selectedCurrencies,
  setSelectedCurrencies,

  ...props
}: InventoryTableProps) {
  // Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<InventoryRecord | null>(
    null
  );
  const [selectedItem, setSelectedItem] = useState<
    | (InventoryRecord & {
        expand: { categories: string[] };
      })
    | null
  >(null);

  // Price range filter management
  const {
    localPriceRange,
    handlePriceRangeChange,
    resetPriceFilter,
    isActiveFilter,
    isPriceRangeValid,
    validationError,
  } = usePriceRangeFilter({
    minMaxPrices,
    setPriceRange,
    setPage,
    priceRange,
  });

  // Price filter popover content
  const priceFilterPopover = ({ close }: { close: () => void }) => (
    <Stack p="xs" w={150}>
      <Group>
        <NumberInput
          label="מחיר מינימלי"
          value={localPriceRange[0]}
          min={0}
          max={localPriceRange[1] ?? minMaxPrices?.[1] ?? 999999}
          onChange={(value) => {
            handlePriceRangeChange([value, localPriceRange[1]]);
          }}
          allowNegative={false}
          thousandSeparator=","
          error={validationError?.min}
          hideControls
        />
        <NumberInput
          label="מחיר מקסימלי"
          value={localPriceRange[1]}
          min={localPriceRange[0] ?? 0}
          max={minMaxPrices?.[1] ?? 999999}
          onChange={(value) => {
            handlePriceRangeChange([localPriceRange[0], value]);
          }}
          allowNegative={false}
          thousandSeparator=","
          error={validationError?.max}
          hideControls
        />
      </Group>
      <Button
        variant="light"
        onClick={() => {
          resetPriceFilter();
          close();
        }}
      >
        איפוס טווח
      </Button>
    </Stack>
  );

  const handleEditClick = (
    item: InventoryRecord & {
      expand: { categories: string[] };
    }
  ) => {
    console.log('Edit clicked, item:', item); // Debug log
    setSelectedItem(item);
    setEditModalOpen(true);
  };

  const handleUpdateSubmit = async (values: FormData) => {
    if (!selectedItem) {
      console.error('No item selected for update');
      return;
    }
    try {
      await updateItemMutation.mutateAsync({
        id: selectedItem.id,
        formData: values,
      });
      setEditModalOpen(false);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message:
          error instanceof Error ? error.message : 'Failed to update item',
        color: 'red',
      });
    }
  };

  const handleDeleteClick = (
    item: InventoryRecord & {
      expand: { categories: string[] };
    }
  ) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  // Get table columns configuration
  const { data, isLoading } = inventoryQuery;
  const categoryRecordsQuery = useGetCategoryRecords();

  const columns = useInventoryColumns({
    nameQuery,
    setNameQuery,
    setPage,
    selectedCategories,
    setSelectedCategories,
    selectedManufacturers,
    setSelectedManufacturers,
    selectedCurrencyTypes,
    setSelectedCurrencyTypes,
    categoriesData: categoriesQuery.data || [],
    manufacturersData: manufacturersQuery.data || [],
    onEditClick: handleEditClick,
    onDeleteClick: handleDeleteClick,
    records: inventoryQuery.data?.items,
    priceFilterPopover,
    isActiveFilter: !!priceRange,
    isPriceRangeValid,
    localPriceRange,
  });

  const { effectiveColumns } = useDataTableColumns({
    key: 'inventory-columns',
    columns,
  });

  return (
    <>
      <ConfirmModal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={() => {
          if (itemToDelete) {
            deleteItemMutation.mutate(itemToDelete.id);
          }
        }}
        variant="delete"
        params={{ itemName: itemToDelete?.name }}
      />

      <EditInventoryModal
        record={selectedItem}
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateSubmit}
        categories={categoryRecordsQuery.data || []}
        manufacturers={manufacturersQuery.data || []}
      />

      <DataTable
        scrollAreaProps={{
          scrollbarSize: '8',
        }}
        withTableBorder
        withColumnBorders
        striped
        highlightOnHover
        records={data?.items}
        columns={effectiveColumns}
        totalRecords={data?.totalItems}
        recordsPerPage={TABLE_CONSTANTS.PAGE_SIZE}
        page={page}
        onPageChange={setPage}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        fetching={isLoading}
        loaderColor="blue"
        noRecordsText="לא נמצאו פריטים המתאימים לסינון"
        pinFirstColumn
        height="calc(100vh - 60px)"
        verticalSpacing="xs"
        storeColumnsKey="inventory-columns"
        paginationSize="xs"
        styles={{
          pagination: {
            direction: 'ltr',
          },
        }}
      />
    </>
  );
}
